import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerRegistryItem } from 'shared/api';
import { $t, translated } from 'shared/lib';
import {
	accountRoute,
	cashdeskRoute,
	deviceRoute,
	gameSettingsRoute,
	playerRoute,
} from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps, SearchInput } from 'shared/ui';
import { PlayerRegistryModel } from '../../model';
import cls from './player-registry.module.scss';

export type PlayerRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	withCashdesk?: boolean;
	withCashdeskFilter?: boolean;
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (player: PlayerRegistryItem) => ReactNode;
};

export const createPlayerRegistry = ({
	model,
	$games,
	$cashdesks,
	$devices,
	$currencies,
}: PlayerRegistryModel) => {
	const Registry = createRegistry(model);

	const NameFilter = reflect({
		view: SearchInput<'input'>,
		bind: {
			'data-test-id': 'player-registry-name-filter',
			label: translated('player-registry.filters.name.label'),
			placeholder: translated('player-registry.filters.name.placeholder'),
			className: cls.playerRegistryFilter,
			value: model.filters.name.$value,
			onChange: (e) => model.filters.name.setValue(e.target.value),
		},
	});

	const StatusesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-registry-statuses-filter',
			className: cls.playerRegistryFilter,
			data: $t.map((t) => [
				{ value: 'true', label: t('player-registry.status.active') },
				{ value: 'false', label: t('player-registry.status.not-active') },
			]),
			value: model.filters.isConnected.$value.map((values) => values.map(String)),
			onChange: (value) => model.filters.isConnected.setValue(value.map((item) => item === 'true')),
			label: translated('player-registry.filters.statuses.label'),
			placeholder: translated('player-registry.filters.statuses.placeholder'),
			nothingFoundMessage: translated('player-registry.filters.statuses.empty'),
			searchable: false,
			clearable: true,
		},
	});

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-registry-currencies-filter',
			className: cls.playerRegistryFilter,
			data: $currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			label: translated('player-registry.filters.currencies.label'),
			placeholder: translated('player-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('player-registry.filters.currencies.empty'),
			searchable: true,
			clearable: true,
		},
	});

	const DevicesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-registry-devices-filter',
			className: cls.playerRegistryFilter,
			data: $devices,
			value: model.filters.deviceIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => model.filters.deviceIds.setValue(value.map(Number)),
			label: translated('player-registry.filters.devices.label'),
			placeholder: translated('player-registry.filters.devices.placeholder'),
			nothingFoundMessage: translated('player-registry.filters.devices.empty'),
			searchable: true,
			clearable: true,
		},
	});

	const GamesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-registry-games-filter',
			className: cls.playerRegistryFilter,
			data: $games,
			value: model.filters.gameIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => model.filters.gameIds.setValue(value.map(Number)),
			label: translated('player-registry.filters.games.label'),
			placeholder: translated('player-registry.filters.games.placeholder'),
			nothingFoundMessage: translated('player-registry.filters.games.empty'),
			searchable: true,
			clearable: true,
		},
	});

	const CashdesksFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-registry-cashdesks-filter',
			className: cls.playerRegistryFilter,
			data: $cashdesks,
			value: model.filters.cashdeskIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => model.filters.cashdeskIds.setValue(value.map(Number)),
			label: translated('player-registry.filters.cashdesks.label'),
			placeholder: translated('player-registry.filters.cashdesks.placeholder'),
			nothingFoundMessage: translated('player-registry.filters.cashdesks.empty'),
			searchable: true,
			clearable: true,
		},
	});

	const PlayerRegistry = forwardRef<HTMLDivElement, PlayerRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				withCashdesk = true,
				withCashdeskFilter = true,
				emptyActionSlot,
				topBarActionSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('player-registry.title')}</Registry.Title>}
					<Registry.TopBar
						alwaysShow
						className={cls.playerRegistryTopBar}
					>
						<Registry.Filters className={cls.playerRegistryFilters}>
							<NameFilter />
							{withCashdeskFilter && <CashdesksFilter />}
							<DevicesFilter />
							<GamesFilter />
							<CurrenciesFilter />
							<StatusesFilter />
						</Registry.Filters>
						<div className={cls.playerRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							{withAccount && <Registry.Table.Col />}
							{withCashdesk && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							{tableTdActionSlot && <Registry.Table.Col />}
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='name'>
									{t('player-registry.table-columns.name')}
								</Registry.Table.ThSorter>
								{withAccount && (
									<Registry.Table.ThSorter field='account'>
										{t('player-registry.table-columns.account')}
									</Registry.Table.ThSorter>
								)}
								{withCashdesk && (
									<Registry.Table.ThSorter field='cashdesk'>
										{t('player-registry.table-columns.cashdesk')}
									</Registry.Table.ThSorter>
								)}
								<Registry.Table.ThSorter field='device'>
									{t('player-registry.table-columns.device')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='game'>
									{t('player-registry.table-columns.game')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='currency'>
									{t('player-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='balance'>
									{t('player-registry.table-columns.balance')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='isConnected'>
									{t('player-registry.table-columns.status')}
								</Registry.Table.ThSorter>
								{tableTdActionSlot && (
									<Registry.Table.Th>
										{t('player-registry.table-columns.actions')}
									</Registry.Table.Th>
								)}
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>
											<Anchor
												data-test-id={`player-registry-player-link-${row.id}`}
												component={Link<StoreValue<typeof playerRoute.$params>>}
												to={playerRoute}
												params={{ id: row.id }}
											>
												{row.name}
											</Anchor>
										</Registry.Table.Td>
										{withAccount && (
											<Registry.Table.Td>
												<Anchor
													data-test-id={`player-registry-account-link-${row.account.id}`}
													component={Link}
													to={accountRoute}
													query={{ id: row.account.id }}
												>
													{row.account.name}
												</Anchor>
											</Registry.Table.Td>
										)}
										{withCashdesk && (
											<Registry.Table.Td>
												<Anchor
													data-test-id={`player-registry-cashdesk-link-${row.cashdesk.id}`}
													component={Link<StoreValue<typeof cashdeskRoute.$params>>}
													to={cashdeskRoute}
													params={{ id: row.cashdesk.id }}
												>
													{row.cashdesk.name}
												</Anchor>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>
											<Anchor
												data-test-id={`player-registry-device-link-${row.device.id}`}
												component={Link<StoreValue<typeof deviceRoute.$params>>}
												to={deviceRoute}
												params={{ id: row.device.id }}
											>
												{row.device.name}
											</Anchor>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<Anchor
												data-test-id={`player-registry-game-link-${row.game.id}`}
												component={Link}
												to={gameSettingsRoute}
												query={{ id: row.game.id }}
											>
												{row.game.title}
											</Anchor>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.balance} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.isConnected
												? t('player-registry.status.active')
												: t('player-registry.status.not-active')}
										</Registry.Table.Td>
										{tableTdActionSlot && (
											<Registry.Table.Td>{tableTdActionSlot(row)}</Registry.Table.Td>
										)}
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('player-registry.empty.title')}
						message={t('player-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('player-registry.table-empty.title')}
						message={t('player-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return PlayerRegistry;
};
